import type { ConfigMotor } from "./config.ts";
import type { NivelRisco } from "./types.ts";

/** Entrada mínima para o cálculo de risco de um item. */
export interface EntradaRisco {
  readonly p: number;
  readonly i: number;
}

/** Risco inerente = P × I (1–25). */
export function riscoInerente({ p, i }: EntradaRisco): number {
  return p * i;
}

/**
 * Risco residual, limitado entre o piso irredutível (30% do inerente) e o
 * próprio inerente. `null` quando o item não tem score (NA/vazio).
 * Arredonda a 1 casa (doc 05/10).
 */
export function riscoResidual(
  entrada: EntradaRisco,
  score: number | null,
  config: ConfigMotor,
): number | null {
  if (score === null) {
    return null;
  }
  const inerente = riscoInerente(entrada);
  const reduzido = inerente * (1 - score * config.residualReducao);
  const limitado = Math.max(reduzido, inerente * config.residualPiso);
  return Math.round(limitado * 10) / 10;
}

/** Classifica um valor de risco em RP/RM/RA/RC. `null` quando sem valor. */
export function nivelRisco(valor: number | null, config: ConfigMotor): NivelRisco | null {
  if (valor === null) {
    return null;
  }
  if (valor <= config.riskRP) {
    return "RP";
  }
  if (valor <= config.riskRM) {
    return "RM";
  }
  if (valor <= config.riskRA) {
    return "RA";
  }
  return "RC";
}

/** Item ganha prioridade de prazo no plano de ação quando o risco atinge o gatilho (≥ 8). */
export function entraNoPlano(valorRisco: number | null, config: ConfigMotor): boolean {
  return valorRisco !== null && valorRisco >= config.gatilhoPlano;
}
